/**
 * meta/challenges.js — three rotating weekly challenges (GDD 8.2).
 *
 * Weekly challenges are the game's Star source. Three are live at a time, drawn
 * from `CHALLENGE_POOL` by a generator seeded from the ISO week itself — so
 * every player sees the same three, they are stable all week, and they change
 * on Monday without anything having to schedule that.
 *
 * Progress is kept as a small set of weekly COUNTERS rather than per-challenge
 * tallies. A challenge's progress is then derived from the counters on read,
 * which means a mid-week rotation (or a save loaded on a different day) can
 * never leave a challenge showing progress it did not earn.
 *
 * @module meta/challenges
 */

import {
  CHALLENGE_POOL, CHALLENGE_COUNT, STALL_IDS, STALL_BY_ID, TOKEN_CURVES,
  EVENTS, META_EVENTS,
} from '../constants.js';
import { save, economy } from '../core/save.js';
import { bus } from '../core/events.js';
import { challengeSlice, weekKey, seededRng, seededShuffle } from './state.js';

/** A fresh counter block for a new week. */
function freshCounters() {
  return { rounds: 0, tokens: 0, plush: 0, fish: 0, variety: [], stallPlays: {}, bestScore: {} };
}

/**
 * Target score for a "score N at <stall>" challenge — a fraction of that
 * stall's GDD 7.3 score ceiling, so the ask always suits the stall.
 * @param {string} stallId
 * @param {number} frac
 * @returns {number}
 */
function scoreTarget(stallId, frac) {
  const curve = TOKEN_CURVES[stallId];
  if (!curve) return 100;
  return Math.max(10, Math.round((curve.scoreMax * frac) / 10) * 10);
}

/**
 * Draw this week's three challenges.
 * @param {string} key ISO week key.
 * @returns {Array<object>}
 */
function generate(key) {
  const gen = seededRng(`challenges:${key}`);
  const picked = seededShuffle([...CHALLENGE_POOL], gen).slice(0, CHALLENGE_COUNT);

  return picked.map((tpl) => {
    const stallId = tpl.stallScoped ? STALL_IDS[Math.floor(gen() * STALL_IDS.length)] : null;
    const target = tpl.kind === 'score' ? scoreTarget(stallId, tpl.scoreFrac) : tpl.n;
    const stallName = stallId ? (STALL_BY_ID[stallId]?.name ?? stallId) : '';
    const text = tpl.text
      .replace('{n}', String(target))
      .replace('{stall}', stallName);
    return {
      id: `${key}:${tpl.id}`,
      templateId: tpl.id,
      kind: tpl.kind,
      stallId,
      target,
      stars: tpl.stars,
      text,
      claimed: false,
    };
  });
}

/** Rebuild the challenge slice if the week has turned over. */
function ensureWeek() {
  const slice = challengeSlice();
  const key = weekKey();
  if (slice.key !== key || !slice.list.length) {
    slice.key = key;
    slice.list = generate(key);
    slice.counters = freshCounters();
    save.markDirty();
  }
  // A save written before a counter existed must not throw on read.
  const base = freshCounters();
  for (const k of Object.keys(base)) {
    if (slice.counters[k] === undefined) slice.counters[k] = base[k];
  }
  return slice;
}

/**
 * How far along a challenge is, derived from the weekly counters.
 * @param {object} ch
 * @param {object} counters
 * @returns {number}
 */
function progressOf(ch, counters) {
  switch (ch.kind) {
    case 'score': return counters.bestScore[ch.stallId] || 0;
    case 'plush': return counters.plush;
    case 'fish': return counters.fish;
    case 'variety': return counters.variety.length;
    case 'rounds': return counters.rounds;
    case 'tokens': return counters.tokens;
    case 'stallPlays': return counters.stallPlays[ch.stallId] || 0;
    default: return 0;
  }
}

/** Pay out anything that just crossed its target. */
function settle() {
  const slice = ensureWeek();
  let granted = 0;
  for (const ch of slice.list) {
    if (ch.claimed) continue;
    if (progressOf(ch, slice.counters) < ch.target) continue;

    ch.claimed = true;
    granted += ch.stars;
    economy.grant({ stars: ch.stars }, `challenge:${ch.templateId}`);
    bus.emit(EVENTS.UI_TOAST, {
      text: `Challenge complete — ${ch.text} (+${ch.stars}★)`,
      kind: 'prize',
      duration: 3600,
    });
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'challenge-complete', stars: ch.stars });
    bus.emit(META_EVENTS.CHALLENGE_COMPLETE, { id: ch.id, stars: ch.stars, text: ch.text });
  }
  if (granted) save.markDirty();
  return granted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Counter updates — one entry point per thing that can happen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fold a completed round into the weekly counters.
 * @param {{ stallId: string, score?: number, tokens?: number }} round
 */
function recordRound({ stallId, score = 0, tokens = 0 }) {
  const slice = ensureWeek();
  const c = slice.counters;
  c.rounds += 1;
  c.tokens += Math.max(0, tokens);
  if (stallId) {
    if (!c.variety.includes(stallId)) c.variety.push(stallId);
    c.stallPlays[stallId] = (c.stallPlays[stallId] || 0) + 1;
    if (score > (c.bestScore[stallId] || 0)) c.bestScore[stallId] = score;
  }
  save.markDirty();
  settle();
}

/**
 * Fold a direct prize (GDD 7.4) into the weekly counters.
 * @param {string} kind `plush-small` | `plush-large` | `fish`
 */
function recordPrize(kind) {
  const slice = ensureWeek();
  if (kind === 'fish') slice.counters.fish += 1;
  else if (String(kind).startsWith('plush')) slice.counters.plush += 1;
  else return;
  save.markDirty();
  settle();
}

let installed = false;

/** Subscribe to the gameplay events challenges are scored from. Idempotent. */
export function installChallengeTracking() {
  if (installed) return;
  installed = true;
  bus.on(EVENTS.STALL_ROUND_END, ({ stallId, score, tokens }) => {
    recordRound({ stallId, score, tokens });
  });
  bus.on(EVENTS.DIRECT_PRIZE, ({ kind }) => recordPrize(kind));
}

export const challenges = {
  /**
   * This week's three challenges with live progress.
   * @returns {Array<object>}
   */
  list() {
    const slice = ensureWeek();
    return slice.list.map((ch) => {
      const progress = progressOf(ch, slice.counters);
      return {
        ...ch,
        progress: Math.min(progress, ch.target),
        rawProgress: progress,
        done: ch.claimed || progress >= ch.target,
        pct: ch.target > 0 ? Math.min(1, progress / ch.target) : 0,
      };
    });
  },

  /** @returns {string} The ISO week the current challenges belong to. */
  weekKey() {
    return ensureWeek().key;
  },

  /** @returns {object} The raw weekly counters — handy in tests. */
  counters() {
    return JSON.parse(JSON.stringify(ensureWeek().counters));
  },

  /** @returns {number} Stars earned from challenges this week. */
  starsEarned() {
    return ensureWeek().list.filter((c) => c.claimed).reduce((a, c) => a + c.stars, 0);
  },

  /**
   * Feed a round in directly. Same path the STALL_ROUND_END subscriber uses —
   * exposed so a test can exercise every challenge kind without needing all
   * twelve stalls implemented.
   * @param {{ stallId: string, score?: number, tokens?: number }} round
   */
  simulateRound(round) {
    recordRound(round);
    return this.list();
  },

  /** As above, for a direct prize. @param {string} kind */
  simulatePrize(kind) {
    recordPrize(kind);
    return this.list();
  },

  /** Force a re-check of every target (after an out-of-band grant). */
  refresh() {
    return settle();
  },
};

export default challenges;
